import ControlledInput from "@/components/Common/form/ControlledInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { ProjectType } from "@/consts/Bid";
import { useCreateProjectGroups } from "@/libs/react-query/hooks/useProjectQuery";
import ControlledAutoComplete from "@/pages/Project/Detail/components/ControlledAutoComplete";
import { parseProjectType } from "@/pages/Project/utils";
import { GetSearchConditionLiveTextSearch } from "@/services/Setting";
import useUserStore from "@/stores/User";
import { CurrentUserContent } from "@/types/setting";
import { Option } from "@/types/Shared/select";
import { showToast } from "@/utils/toastUtils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { z } from "zod";

const formSchema = z.object({
  title: z.string().min(1, {
    message: "프로젝트 이름을 입력해주세요.",
  }),
  participants: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
      })
    )
    .min(1, {
      message: "참여자를 추가해주세요.",
    }),
});

type FormSchema = z.infer<typeof formSchema>;

export default function CreateProjectDialog({
  bidTitle,
}: {
  bidTitle: string;
}) {
  const { projectType, projectId = "" } = useParams();

  const storedUser: CurrentUserContent = useUserStore((state) => state.user);

  const [open, setOpen] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [commands, setCommands] = useState<Option[]>([]);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      title: bidTitle,
      participants: [],
    },
  });
  const {
    handleSubmit,
    formState: { isValid, isDirty },
  } = form;

  const { mutate } = useCreateProjectGroups({
    project_id: projectId,
    project_type: parseProjectType(projectType as ProjectType),
  });

  const fetchSearchByName = useCallback(async (name: string) => {
    if (!projectId) {
      return;
    }

    try {
      const result = await GetSearchConditionLiveTextSearch(name);
      const parseCommands = result.data.pods[0].content.map((item) => ({
        value: item.user_id,
        label: item.last_name + item.first_name,
        ...item,
      }));

      setCommands(parseCommands);
    } catch (error) {
      console.error("error > ", error);
    }
  }, []);

  useEffect(() => {
    if (
      storedUser?.user_id &&
      storedUser?.first_name &&
      storedUser?.last_name
    ) {
      form.reset({
        title: bidTitle,
        participants: [
          {
            value: storedUser.user_id,
            label: storedUser.last_name + storedUser.first_name,
          },
        ],
      });
    }
  }, [storedUser, bidTitle]);

  useEffect(() => {
    fetchSearchByName(searchName);
  }, []);

  const onOpenChange = (value: boolean) => {
    setOpen(value);
    if (!value) {
      form.reset();
      setSearchName("");
    }
  };

  const onSubmit = async (data: FormSchema) => {
    if (!projectId) return;

    const body = {
      title: data.title,
      users: data.participants.map(({ value }) => ({
        user_id: value,
      })),
    };

    try {
      mutate(body, {
        onSuccess: () => {
          showToast({ message: "프로젝트 생성이 완료되었습니다" });
          setOpen(false);
        },
      });
    } catch (error) {}
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <span className="px-3 py-[6px] rounded-sm bg-blue-ThermoFisher hover:bg-blue-ThermoFisher/80 text-white text-sm font-semibold cursor-pointer">
          프로젝트 생성하기
        </span>
      </DialogTrigger>
      <DialogContent
        className="[&>button]:hidden max-h-[780px] min-h-[450px] w-[640px] overflow-auto"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="h-full flex flex-col gap-y-4">
              <DialogHeader>
                <DialogTitle>프로젝트 생성하기</DialogTitle>
                <DialogDescription />
              </DialogHeader>

              <div className="flex-1 flex flex-col gap-y-4">
                <ControlledInput
                  label="프로젝트 이름 설정하기"
                  name="title"
                  placeholder="프로젝트 이름을 설정하세요"
                />
                <ControlledAutoComplete
                  label="참여자 추가하기"
                  name="participants"
                  placeholder="이름으로 검색해보세요"
                  commands={commands}
                  inputValue={searchName}
                  setInputValue={setSearchName}
                />
              </div>

              <DialogFooter>
                <Button
                  size="md"
                  className="bg-gray-E hover:bg-gray-E/80 border-gray-D border text-gray-A rounded-md"
                  onClick={() => onOpenChange(false)}
                >
                  취소하기
                </Button>
                <Button
                  size="md"
                  className="bg-dark-red hover:bg-dark-red/80 border-dark-red border rounded-md"
                  disabled={!isValid}
                  type="submit"
                >
                  생성하기
                </Button>
              </DialogFooter>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
